import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape CUS-01 publishes, imported rather than restated.
import type { CustomerProfile } from "@tdelivr/api-client/generated/customer/customer-profile.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { profileRow, PROFILE_STATUSES } from "../customers.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Customers: this tenant's riders and senders, newest first.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `customers`, against CUS-01
 * `GET /v1/customer-profiles`.
 *
 * **Two scopes, and this is the one page of the fifteen whose listing cost a scope nothing else
 * needed.** `customer-profile:read` is an optional client scope on `tdelivr-customer-app` — a
 * rider's own phone — so a roster granted to every holder of it would hand a phone every customer's
 * name in the tenant. The route requires `customer-profile:roster` in addition, which no mobile
 * client holds, and `navigation.ts` asks for both: `resolvePage` treats a partly-held requirement
 * as a refusal rather than as partial access, so an administrator with one of the two does not see
 * this page.
 *
 * **`Phone` has no field anywhere, and the design masks it.** Q-103, whose recorded decision is
 * that the column carries the absence rather than a masked blank — `+254 7•• ••• •••` would read as
 * a number this console is holding and hiding, which is the one wrong answer here.
 *
 * **The design's own sentence about masking and auditing is replaced, because it is a safety
 * claim.** "Personal data is masked until you open a record, and opening it is audited." There is
 * nothing to mask, and opening a record is not audited: CUS-01 writes its `protected_audit_log`
 * from `recordSideEffects`, which runs inside `update`. A change is audited; a read is not. An
 * administrator who believes their colleagues' lookups are logged will behave differently from one
 * who knows they are not. Q-107.
 *
 * **None of the design's four tiles has a source.** `182,440` and `3,902` are counts no operation
 * answers; `Deletion requests · 4` is nearest to `closure_pending`, and the design's "30-day clock"
 * is a legal deadline this service does not hold; `Blocked · 61` spans `restricted` and
 * `suspended`, which are two states and one tile.
 */

const profilesApi = services.customer__customer_profile;

export interface CustomersProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly profiles: readonly CustomerProfile[];
  readonly truncated: boolean;
}

export function Customers({ transport, crumbs }: CustomersProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const client = profilesApi.createCustomerProfileClient(transport);
    try {
      /*
       * The status filter goes to the service; `identitySubjectId` deliberately does not.
       *
       * That parameter is the lookup that was also missing before R-39 — it is how a signed-in
       * subject is resolved to a record at all — and it is not a filter a roster offers: an
       * administrator holding a subject identifier is not browsing. The board's search box searches
       * the rows it holds, which includes each one's subject because the detail panel lists it.
       */
      const answered = await client.listCustomerProfiles(
        status === "" ? undefined : status,
        undefined,
        PER_FETCH,
        undefined,
      );
      setLoaded({
        profiles: answered.customerProfiles,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["customer-profile:read", "customer-profile:roster"]));
      setLoaded(undefined);
    }
  }, [transport, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return loaded.profiles.map((profile) => profileRow(profile));
  }, [loaded]);

  const bounded = boundedNote(
    loaded?.profiles.length,
    loaded?.truncated ?? false,
    status === "" ? "customers" : `${status} customers`,
    "newest first",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Customers"
      source="CUS-01"
      /*
       * The design's sentence is replaced, and this is the one place this page's copy is not the
       * design's. "Your riders and senders. Personal data is masked until you open a record, and
       * opening it is audited." The first clause is kept; the rest is a promise of two protections
       * that are not here. Q-107.
       */
      description="Your riders and senders. CUS-01 publishes a display name and no contact details, and it audits changes rather than lookups."
      columns={["Customer", "Phone", "Joined"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Customers appear here as soon as one signs up or is added for this tenant."
      primary={{
        label: "Add customer",
        /*
         * Drawn, and refused. `POST /v1/customer-profiles` exists and requires an
         * `identitySubjectId` — the subject Keycloak issues for a person who has signed up (ADR
         * 0014) — which a form taking a name and a phone number cannot produce. The design's own
         * fields make the mismatch plain: `Mobile` is required and has no field (Q-103), `Profile
         * photo` has no field, and both consent toggles are "Recorded with your name and the
         * time", which is the customer-consent service rather than this one.
         *
         * An `Idempotency-Key` would have been required — the contract says so in the operation's
         * own description — and there is no command to put one on.
         */
        refusal:
          "CUS-01's create requires an identitySubjectId, which Keycloak issues when a person signs up (ADR 0014) and no form here can produce. Its Mobile, Profile photo and consent fields have no field on this service. Q-103.",
      }}
      filter={{
        unfiltered: "All states",
        options: PROFILE_STATUSES.map((value) => ({ value, label: value })),
        value: status,
        onChange: setStatus,
      }}
      note={{
        /*
         * The design's `lock` note is replaced and keeps its kind, its glyph and its place.
         *
         * It reads "Deletion requests run on a legal clock; the row shows the days left." CUS-01
         * publishes `updatedAt` and no deadline of any kind — no statutory period, no due date, no
         * jurisdiction — so a row counting days would be this console inventing a legal deadline.
         * Q-107.
         */
        kind: "lock",
        glyph: "lock",
        text: "A customer in closure pending has asked to be deleted. CUS-01 records when that was asked for and no deadline, so the days left are not here.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every customer profile this tenant holds, newest first. This board needs customer-profile:roster as well as customer-profile:read, which no mobile client is granted."
      exportName="customers"
      detailSubtitle="Customers · CUS-01"
      detailNote="A customer in closure pending has asked to be deleted. CUS-01 records when that was asked for and no deadline, so the days left are not here."
    />
  );
}
